import { useMemo } from "react";
import { useTranslations } from "@zapier/i18n";
import { Text } from "@zapier/design-system";
import { AssetTypes } from "@zapier/utils";
import { type LinkedAsset } from "./LinkedAssetsWarning";
import { LinkedAssetsAccordion } from "./LinkedAssetsAccordion";
import styles from "./LinkedAssetsSection.module.css";

interface LinkedAssetsSectionProps {
  hasBlockingConnections: boolean;
  hasNonBlockingConnections: boolean;
  transformedAssets: LinkedAsset[];
}

/**
 * Renders connected (non-view) assets: a bold intro line followed by a
 * "Dependencies" accordion with a count badge. Views are called out in the
 * modal's own explanation copy instead, not here. Renders nothing when there
 * are no connections.
 */
export function LinkedAssetsSection({
  hasBlockingConnections,
  hasNonBlockingConnections,
  transformedAssets,
}: LinkedAssetsSectionProps) {
  const t = useTranslations("linked-assets-warning");
  const hasConnections = hasBlockingConnections || hasNonBlockingConnections;

  const connectionAssets = useMemo(
    () =>
      transformedAssets.filter((asset) => asset.type !== AssetTypes.TableView),
    [transformedAssets],
  );

  if (!hasConnections) {
    return null;
  }

  return (
    <div className={styles.accordionContainer}>
      <Text type="Body3">{t("messages.unlink-related-assets-warning")}</Text>
      <LinkedAssetsAccordion
        label={t("messages.dependencies-header")}
        linkedAssets={connectionAssets}
      />
    </div>
  );
}
